import moment from 'moment'
import { IBillDocument, IExtractedField } from '../services/file-upload/file-upload.model'

/**
 * Bill uploads (PDF/image bills read by the backend) shown after the read: what became of each
 * bill, and how its rows read. Shared by the import wizard's review step and the import history
 * page, so both say the same thing about the same upload.
 */

// Human labels for the extraction field keys. Anything not listed is internal and not worth showing.
export const BILL_FIELD_LABELS: Record<string, string> = {
  SUPPLIER: 'Supplier',
  ACCOUNT_NUMBER: 'Account number',
  INVOICE_NUMBER: 'Invoice number',
  INVOICE_DATE: 'Invoice date',
  PERIOD_START: 'Billing period start',
  PERIOD_END: 'Billing period end',
  SUPPLY_ADDRESS: 'Supply address',
  MPAN: 'MPAN (electricity meter point)',
  MPRN: 'MPRN (gas meter point)',
  METER_SERIAL: 'Meter serial',
  READ_TYPE: 'Meter reading',
  FUEL_TYPE: 'Fuel type',
  VOLUME_LITRES: 'Volume (litres)',
  VEHICLE_REGISTRATION: 'Vehicle registration',
  FUEL_CARD_NUMBER: 'Fuel card number',
  TRANSACTION_DATE: 'Transaction date',
  TOTAL_COST: 'Total cost',
  TAX: 'Tax',
}

// The fields that drive the emission, worth a reviewer's attention. The consumption figure is not
// here: it comes from the reconciling charge lines, not a single field, and is shown on the row.
export const BILL_KEY_FIELDS = ['INVOICE_NUMBER', 'PERIOD_START', 'PERIOD_END', 'MPAN', 'MPRN', 'VOLUME_LITRES', 'SUPPLY_ADDRESS', 'SUPPLIER', 'READ_TYPE']

export const BILL_CONFIDENCE_THRESHOLD = 80

export const isLowConfidence = (field?: IExtractedField | null) =>
  !!field && typeof field.confidence === 'number' && field.confidence < BILL_CONFIDENCE_THRESHOLD

/** The key fields read off a bill with low confidence: a prompt to look, never a block. */
export const lowConfidenceFields = (document: IBillDocument): string[] => {
  const fields = document.extractedFields || {}
  return BILL_KEY_FIELDS.filter((key) => isLowConfidence(fields[key]))
}

export const isBillReading = (document: IBillDocument) =>
  document.extractionStatus === 'PENDING' || document.extractionStatus === 'IN_PROGRESS'

export type BillOutcome =
  | 'reading'
  | 'unreadable'
  | 'credit-note'
  | 'cancelled'
  | 'needs-attention'
  | 'ready'
  | 'imported'
  | 'discarded'
  | 'no-rows'

/** What became of one bill, from its own state and its rows' counts. */
export const billOutcome = (document: IBillDocument): BillOutcome => {
  if (isBillReading(document)) return 'reading'
  const outstanding = document.outstandingRowCount || 0
  const attention = document.attentionRowCount || 0
  const committed = document.committedRowCount || 0
  const discarded = document.discardedRowCount || 0
  if (document.isCreditNote && outstanding === 0 && committed === 0) return 'credit-note'
  if (document.cancelledByBillDocumentId && outstanding > 0) return 'cancelled'
  if (document.extractionStatus === 'FAILED' && outstanding > 0) return 'unreadable'
  if (attention > 0) return 'needs-attention'
  if (outstanding > 0) return 'ready'
  if (committed > 0) return 'imported'
  if (discarded > 0) return 'discarded'
  return 'no-rows'
}

export const BILL_OUTCOME_LABELS: Record<BillOutcome, string> = {
  'reading': 'Reading',
  'unreadable': "Couldn't read",
  'credit-note': 'Credit note',
  'cancelled': 'Cancelled by credit note',
  'needs-attention': 'Needs attention',
  'ready': 'Ready',
  'imported': 'Imported',
  'discarded': 'Discarded',
  'no-rows': 'No rows',
}

export const BILL_OUTCOME_COLOURS: Record<BillOutcome, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  'reading': 'default',
  'unreadable': 'error',
  'credit-note': 'info',
  'cancelled': 'warning',
  'needs-attention': 'error',
  'ready': 'success',
  'imported': 'success',
  'discarded': 'default',
  'no-rows': 'default',
}

/**
 * A bill that needs a person to look at it before the upload is done with. A value read with low
 * confidence is not enough on its own: nearly every bill has one (a supplier name, an address), and
 * counting them buried the real jobs. The bill's panel still marks each one.
 */
export const billNeedsALook = (document: IBillDocument) => {
  const outcome = billOutcome(document)
  return outcome === 'unreadable'
    || outcome === 'credit-note'
    || outcome === 'cancelled'
    || outcome === 'needs-attention'
    || (outcome === 'ready' && !!document.duplicateOfBillDocumentId)
}

/** The activity a staged bill row will book against. Fuel rows carry their unit on the rule. */
export const billLineActivity = (line: any): string =>
  line?.ruleName || line?.detailLevelThree || line?.categoryName || line?.groupName || ''

export const billLineUsage = (line: any): string => {
  if (!(line?.unitsConsumed || line?.unitsConsumed === 0)) return ''
  const unitName = line.measurementUnitName || line.rule?.measurementUnitName || ''
  return `${Number(line.unitsConsumed).toLocaleString(undefined, { maximumFractionDigits: 2 })}${unitName ? ` ${unitName}` : ''}`
}

export const billLinePeriod = (line: any): string => {
  if (!line?.startDate) return ''
  return `${moment(line.startDate).format('DD/MM/YYYY')}${line.endDate ? ` to ${moment(line.endDate).format('DD/MM/YYYY')}` : ''}`
}

export const billDocumentPeriod = (document: IBillDocument): string => {
  if (!document.periodStart && !document.periodEnd) return ''
  const start = document.periodStart ? moment(document.periodStart).format('DD/MM/YYYY') : '?'
  const end = document.periodEnd ? moment(document.periodEnd).format('DD/MM/YYYY') : '?'
  return `${start} to ${end}`
}

/** "2 rows: 1 imported, 1 waiting" */
export const billDocumentRowsText = (document: IBillDocument): string => {
  const staged = document.stagedRowCount || 0
  if (staged === 0) return 'None'
  const parts: string[] = []
  const committed = document.committedRowCount || 0
  const outstanding = document.outstandingRowCount || 0
  const discarded = document.discardedRowCount || 0
  if (committed > 0) parts.push(`${committed} imported`)
  if (outstanding > 0) parts.push(`${outstanding} waiting`)
  if (discarded > 0) parts.push(`${discarded} discarded`)
  return parts.length === 1 && staged === 1 ? parts[0].replace(/^1 /, '').replace(/^./, (c) => c.toUpperCase()) : `${staged}: ${parts.join(', ')}`
}
